import { useState, useEffect, useRef } from 'react';
import { router } from '@inertiajs/react';

const quickActions = [
    { icon: 'bi-plus-circle', title: 'Create Contact', subtitle: 'Add a new contact', href: '/contacts/create', shortcut: ['C'] },
    { icon: 'bi-building', title: 'Create Company', subtitle: 'Add a new company', href: '/companies/create', shortcut: ['O'] },
    { icon: 'bi-currency-dollar', title: 'Create Deal', subtitle: 'Start a new deal', href: '/deals/create', shortcut: ['D'] },
    { icon: 'bi-calendar-plus', title: 'Schedule Meeting', subtitle: 'Book a meeting', href: '/meetings/create', shortcut: ['M'] },
];

const recentPages = [
    { icon: 'bi-grid-1x2', title: 'Dashboard', href: '/' },
    { icon: 'bi-people', title: 'Contacts', href: '/contacts' },
    { icon: 'bi-currency-dollar', title: 'Deals Pipeline', href: '/deals' },
    { icon: 'bi-airplane', title: 'Bookings', href: '/bookings' },
];

export default function SearchModal({ isOpen, onClose }) {
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setQuery('');
            setActiveIndex(0);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            const totalItems = quickActions.length + recentPages.length;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex(prev => (prev + 1) % totalItems);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex(prev => (prev - 1 + totalItems) % totalItems);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const allItems = [...quickActions, ...recentPages];
                if (allItems[activeIndex]) {
                    router.visit(allItems[activeIndex].href);
                    onClose();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, activeIndex, onClose]);

    const handleItemClick = (href) => {
        router.visit(href);
        onClose();
    };

    const filteredActions = quickActions.filter(item =>
        item.title.toLowerCase().includes(query.toLowerCase())
    );

    const filteredPages = recentPages.filter(item =>
        item.title.toLowerCase().includes(query.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <>
            <div className="search-modal-backdrop show" onClick={onClose}></div>
            <div className="search-modal show">
                <div className="search-input-wrapper">
                    <i className="bi bi-search"></i>
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search or type a command..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <kbd>ESC</kbd>
                </div>

                <div className="search-results">
                    {filteredActions.length > 0 && (
                        <div className="search-section">
                            <div className="section-title">Quick Actions</div>
                            {filteredActions.map((item, idx) => (
                                <div
                                    key={idx}
                                    className={`search-item ${activeIndex === idx ? 'active' : ''}`}
                                    onClick={() => handleItemClick(item.href)}
                                    onMouseEnter={() => setActiveIndex(idx)}
                                >
                                    <div className="item-icon">
                                        <i className={`bi ${item.icon}`}></i>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-title">{item.title}</div>
                                        <div className="item-subtitle">{item.subtitle}</div>
                                    </div>
                                    {item.shortcut && (
                                        <div className="item-shortcut">
                                            {item.shortcut.map((key, i) => (
                                                <kbd key={i}>{key}</kbd>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {filteredPages.length > 0 && (
                        <div className="search-section">
                            <div className="section-title">Recent Pages</div>
                            {filteredPages.map((item, idx) => {
                                const itemIndex = quickActions.length + idx;
                                return (
                                    <div
                                        key={idx}
                                        className={`search-item ${activeIndex === itemIndex ? 'active' : ''}`}
                                        onClick={() => handleItemClick(item.href)}
                                        onMouseEnter={() => setActiveIndex(itemIndex)}
                                    >
                                        <div className="item-icon">
                                            <i className={`bi ${item.icon}`}></i>
                                        </div>
                                        <div className="item-content">
                                            <div className="item-title">{item.title}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {query && filteredActions.length === 0 && filteredPages.length === 0 && (
                        <div className="empty-state py-4">
                            <div className="empty-icon" style={{ width: 60, height: 60, fontSize: '1.5rem' }}>
                                <i className="bi bi-search"></i>
                            </div>
                            <div className="empty-title">No results found</div>
                            <div className="empty-text">Try searching for something else</div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
